import { JUDGMENT_COLUMNS } from '@/components/judgments/columns'
import { StatusDot } from '@/components/judgments/StatusBadge'
import { Skeleton } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'
import { EMPTY, formatMoney, formatNames } from '@/lib/format'
import type { Judgment, SortDirection, SortField } from '@/types/api'

export interface JudgmentTableProps {
  judgments: Judgment[]
  sortBy: SortField
  sortDir: SortDirection
  onSort: (field: SortField) => void
  onSelect: (judgment: Judgment) => void
  selectedId?: number | null
  loading?: boolean
  pageSize?: number
}

function SortIndicator({ active, direction }: { active: boolean; direction: SortDirection }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={cn(
        'h-3 w-3 shrink-0 transition-[opacity,rotate] duration-150 ease-out',
        active ? 'text-accent' : 'text-ink-muted opacity-0 group-hover:opacity-60',
        active && direction === 'asc' && 'rotate-180',
      )}
      aria-hidden="true"
    >
      <path d="M6 8.5L3 5h6z" fill="currentColor" />
    </svg>
  )
}

/**
 * One result, as a card.
 *
 * Below `md` the table would need ~36rem of horizontal scrolling, which on a
 * phone means the judgment number and the amount are never on screen together
 * — and those are the two fields a decision is made on. The card puts them on
 * the same line instead.
 */
function JudgmentCard({
  judgment,
  selected,
  onSelect,
}: {
  judgment: Judgment
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Open judgment ${judgment.judgmentNumber || judgment.id}`}
      className={cn(
        'flex w-full flex-col gap-1.5 border-b border-line px-4 py-3 text-left transition-colors',
        selected ? 'bg-accent-soft' : 'hover:bg-surface-hover active:bg-surface-active',
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="ident truncate font-medium text-ink">
          {judgment.judgmentNumber || EMPTY}
        </span>
        <span className="ident shrink-0 font-medium text-ink">
          {judgment.amountValue ? formatMoney(judgment.amountValue) : EMPTY}
        </span>
      </div>
      <p className="truncate text-sm text-ink-secondary">
        {formatNames(judgment.debtorNames, 1)}
      </p>
      <div className="flex items-center justify-between gap-3 text-xs">
        <StatusDot status={judgment.status} statusText={judgment.statusText} />
        <span className="ident shrink-0 text-ink-muted">
          {judgment.dateEntered || EMPTY}
        </span>
      </div>
    </button>
  )
}

/**
 * The results grid.
 *
 * Header and body are both generated from `JUDGMENT_COLUMNS`, so they cannot
 * disagree. Sorting and paging are server-side; the header only reports
 * intent, it never re-orders the page it already holds, which would sort the
 * visible rows alone and quietly mislead.
 */
export function JudgmentTable({
  judgments,
  sortBy,
  sortDir,
  onSort,
  onSelect,
  selectedId,
  loading,
  pageSize = 25,
}: JudgmentTableProps) {
  const showSkeletons = loading && judgments.length === 0
  const skeletonRows = Math.min(pageSize, 10)

  return (
    <>
      {/* -- phones: a card per result ---------------------------------- */}
      <div className="md:hidden">
        {showSkeletons
          ? Array.from({ length: skeletonRows }, (_, index) => (
              <div key={index} className="flex flex-col gap-2 border-b border-line px-4 py-3">
                <Skeleton className="h-3.5 w-2/5" />
                <Skeleton className="h-3 w-4/5" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))
          : judgments.map((judgment) => (
              <JudgmentCard
                key={judgment.id}
                judgment={judgment}
                selected={selectedId === judgment.id}
                onSelect={() => onSelect(judgment)}
              />
            ))}
      </div>

      {/* -- tablet and up: the grid ------------------------------------ */}
      <div className="scrollbar-slim hidden overflow-x-auto md:block xl:overflow-x-visible">
        <table className="w-full min-w-[36rem] table-fixed border-collapse">
          <caption className="sr-only">
            Judgment search results. Use the column headers to change the sort order.
          </caption>

          {/* Sticky only where the wrapper is not itself a scroll container:
              `overflow-x: auto` implies `overflow-y: auto`, which would pin the
              header to a box that never scrolls vertically. */}
          <thead className="xl:sticky xl:top-14 xl:z-(--z-sticky)">
            <tr className="bg-surface-sunken">
              {JUDGMENT_COLUMNS.map((column) => {
                const active = column.sortField === sortBy
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={
                      active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    style={column.width ? { width: column.width } : undefined}
                    className={cn(
                      'border-b border-line px-3 py-2 text-2xs font-semibold tracking-[0.06em] whitespace-nowrap text-ink-muted uppercase',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      column.responsiveClass,
                    )}
                  >
                    {column.sortField ? (
                      <button
                        type="button"
                        onClick={() => onSort(column.sortField as SortField)}
                        className={cn(
                          'group inline-flex items-center gap-1 transition-colors hover:text-ink',
                          column.align === 'right' && 'flex-row-reverse',
                          active && 'text-ink',
                        )}
                      >
                        {column.header}
                        <SortIndicator active={active} direction={sortDir} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {showSkeletons
              ? Array.from({ length: skeletonRows }, (_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-line-subtle">
                    {JUDGMENT_COLUMNS.map((column) => (
                      <td key={column.id} className={cn('px-3 py-2.5', column.responsiveClass)}>
                        <Skeleton className="h-3 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : judgments.map((judgment) => (
                  <tr
                    key={judgment.id}
                    onClick={() => onSelect(judgment)}
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelect(judgment)
                      }
                    }}
                    aria-label={`Open judgment ${judgment.judgmentNumber || judgment.id}`}
                    className={cn(
                      'cursor-pointer border-b border-line-subtle transition-colors duration-75',
                      selectedId === judgment.id
                        ? 'bg-accent-soft'
                        : 'hover:bg-surface-hover',
                    )}
                  >
                    {JUDGMENT_COLUMNS.map((column) => (
                      <td
                        key={column.id}
                        title={column.title?.(judgment) || undefined}
                        className={cn(
                          'overflow-hidden px-3 py-2.5 whitespace-nowrap',
                          column.responsiveClass,
                          column.cellClass,
                        )}
                      >
                        {column.cell(judgment)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
